import { C, fmt, useIsMobile } from "./ui";

const diasHasta = (fechaISO) => {
  if (!fechaISO) return null;
  const ms = new Date(fechaISO + "T12:00:00").getTime() - new Date().setHours(12, 0, 0, 0);
  return Math.round(ms / 86400000);
};

const textoFecha = (d) => {
  if (d === 0) return "Hoy";
  if (d === 1) return "Mañana";
  if (d === -1) return "Ayer";
  if (d < 0) return `Hace ${-d}d`;
  return `En ${d}d`;
};

const ICON_TAREA = {
  "Vacunación": "💉", "Desparasitación": "🪱", "Entore": "🐂", "Parición": "🐮",
  "Destete": "🍼", "Análisis / Diagnóstico": "🔬", "Recorrida": "🚜",
  "Aplicación sanitaria": "🧴", "Otro": "📌",
};

export function MonitorPanel({ tareas, empleados, maquinas = [] }) {
  const isMobile = useIsMobile();

  const tareasProximas = tareas.filter(t => {
    const d = diasHasta(t.fecha);
    return d !== null && d >= 0 && d <= 7 && t.estado !== "hecha";
  }).sort((a, b) => a.fecha.localeCompare(b.fecha));

  // Agrupar por empleado
  const porEmpleado = empleados.reduce((acc, emp) => {
    const tareasDel = tareasProximas.filter(t => t.empleadoId === emp.id);
    if (tareasDel.length > 0) {
      acc.push({ empleado: emp, tareas: tareasDel });
    }
    return acc;
  }, []);

  // Tareas sin asignar
  const sinAsignar = tareasProximas.filter(t => !t.empleadoId);

  // Agrupar por máquina (si hay)
  const porMaquina = maquinas && maquinas.length > 0 ? maquinas.reduce((acc, maq) => {
    const tareasDel = tareasProximas.filter(t => t.maquinaId === maq.id);
    if (tareasDel.length > 0) {
      acc.push({ maquina: maq, tareas: tareasDel });
    }
    return acc;
  }, []) : [];

  const TareaCard = ({ t, showEmpleado = false }) => {
    const d = diasHasta(t.fecha);
    const col = d < 0 ? C.danger : d <= 1 ? "#E8B400" : d <= 3 ? "#9A7B1E" : C.tierra;
    return (
      <div style={{
        background: C.white, borderRadius: 8, padding: 12, marginBottom: 8,
        border: `2px solid ${col}`, display: "flex", gap: 10, alignItems: "flex-start"
      }}>
        <span style={{ fontSize: 20, flexShrink: 0 }}>{ICON_TAREA[t.tipo] || "📌"}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.tierra }}>{t.titulo}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            {t.tipo} · {fmt(t.fecha)}
          </div>
          {t.obs && <div style={{ fontSize: 11, color: C.muted, marginTop: 2, fontStyle: "italic" }}>"{t.obs}"</div>}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: col, whiteSpace: "nowrap" }}>
          {textoFecha(d)}
        </div>
      </div>
    );
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", background: C.fondo }}>
      <div style={{ padding: isMobile ? "14px 14px" : "20px 24px", maxWidth: 920, margin: "0 auto" }}>
        <div style={{ fontFamily: "serif", fontSize: 22, color: C.tierra, fontWeight: 600, marginBottom: 4 }}>
          📊 Monitor
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
          Próximas 7 días — tareas por empleado y máquina.
        </div>

        {tareasProximas.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: C.muted }}>
            <div style={{ fontSize: 48, marginBottom: 10 }}>✓</div>
            <div style={{ fontSize: 14 }}>Sin tareas agendadas para la próxima semana.</div>
          </div>
        ) : (
          <>
            {/* Por Empleado */}
            {porEmpleado.length > 0 && (
              <div style={{ marginBottom: 30 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.tierra, marginBottom: 14, paddingBottom: 8, borderBottom: `2px solid ${C.arenaDark}` }}>
                  👤 Por Empleado
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(350px, 1fr))", gap: 20 }}>
                  {porEmpleado.map(({ empleado, tareas: tareasEmp }) => (
                    <div key={empleado.id} style={{ background: C.white, borderRadius: 12, padding: 14, border: `1px solid ${C.arenaDark}` }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: C.tierra, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 18 }}>👤</span>
                        {empleado.nombre}
                        <span style={{ fontSize: 11, fontFamily: "monospace", background: C.arena, padding: "2px 6px", borderRadius: 4, marginLeft: "auto", color: C.muted }}>
                          {tareasEmp.length} tarea{tareasEmp.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      {tareasEmp.map(t => <TareaCard key={t.id} t={t} />)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Por Máquina */}
            {porMaquina.length > 0 && (
              <div style={{ marginBottom: 30 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.tierra, marginBottom: 14, paddingBottom: 8, borderBottom: `2px solid ${C.arenaDark}` }}>
                  🚜 Por Máquina
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(350px, 1fr))", gap: 20 }}>
                  {porMaquina.map(({ maquina, tareas: tareasMaq }) => (
                    <div key={maquina.id} style={{ background: C.white, borderRadius: 12, padding: 14, border: `1px solid ${C.arenaDark}` }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: C.tierra, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 18 }}>🚜</span>
                        {maquina.nombre}
                        <span style={{ fontSize: 11, fontFamily: "monospace", background: C.arena, padding: "2px 6px", borderRadius: 4, marginLeft: "auto", color: C.muted }}>
                          {tareasMaq.length} tarea{tareasMaq.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      {tareasMaq.map(t => <TareaCard key={t.id} t={t} />)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sin Asignar */}
            {sinAsignar.length > 0 && (
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.tierra, marginBottom: 14, paddingBottom: 8, borderBottom: `2px solid ${C.arenaDark}` }}>
                  📋 Sin Asignar
                </div>
                <div style={{ background: C.white, borderRadius: 12, padding: 14, border: `1px solid ${C.arenaDark}` }}>
                  {sinAsignar.map(t => <TareaCard key={t.id} t={t} />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
